import {Injectable} from "@angular/core";
import {StorageService} from "./storage.service";
import {LOCAL_STORAGE} from "../constants";

export interface IPermission {
	name: string
}

@Injectable({
	providedIn: 'root'
})
export class SessionService {
	private _permissions: Array<IPermission>;
	private _userType: string;
	private _userId: string;
	private _accessTokenExpirationTime: number;
	private _refreshTokenExpiration: number;
	private _currentOrg: string;

	constructor(
		private storageService: StorageService
	) {
		this.loadValuesFromLocalstorage();
	}

	private loadValuesFromLocalstorage() {
		const token = this.storageService.getLocalStorage('idToken');
		if(!token) {
			return;
		}

		const data = JSON.parse(atob(token));
		this._permissions = data.permissions;
		this._userId = data.userId;
		this._userType = data.lastSelectedOrganization.type;
		this._accessTokenExpirationTime = data.accessTokenExpiration;
		this._refreshTokenExpiration = data.refreshTokenExpiration;
		this._currentOrg = data.lastSelectedOrganization.id;
	}

	set idToken(token: string) {
		this.storageService.setLocalStorage('idToken', token);
		this.loadValuesFromLocalstorage();
	}

	/**
	 * return tru if both tokens are available and enough time is left to use them.
	 */
	isAuthenticated(): boolean {
		return this.remainingAccessTokenTime > 60 && this.remainingRefreshTokenTime > 600;
	}

	/**
	 * return the remaining time of the AccessToken expiration time in seconds.
	 * returns zero if no accessToken is available.
	 * may return a negativ number if the token is already expired.
	 */
	get remainingAccessTokenTime(): number {
		let accessTokenExpirationTime = this.accessTokenExpirationTime;
		if (!accessTokenExpirationTime) {
			accessTokenExpirationTime = 0;
		}
		return accessTokenExpirationTime - (Date.now()/1000);
	}

	/**
	 * return the remaining time of the RefreshToken expiration time in se
	 * returns zero if no refreshToken is available.
	 * may return a negativ number if the token is already expired.
	 */
	get remainingRefreshTokenTime(): number {
		let refreshTokenExpirationTime = this.refreshTokenExpirationTime;
		if (!refreshTokenExpirationTime) {
			refreshTokenExpirationTime = 0;
		}
		return refreshTokenExpirationTime - (Date.now()/1000);
	}

	/**
	 * return true if both, and only if both, tokens are available regardless of there expiration.
	 */
	get tokenAvailable(): boolean {
		return this.accessTokenExpirationTime !== undefined && this.refreshTokenExpirationTime !== undefined;
	}

	get currentOrgId() {
		return this._currentOrg;
	}

	get userId() {
		return this._userId;
	}

	get permissions() {
		return this._permissions;
	}

	get userType() {
		return this._userType;
	}

	get accessTokenExpirationTime() {
		return this._accessTokenExpirationTime;
	}

	get refreshTokenExpirationTime() {
		return this._refreshTokenExpiration;
	}

	set language(language: string) {
		this.storageService.setLocalStorage(LOCAL_STORAGE.LANGUAGE, language);
	}

	get language() {
		let browserLanguage = this.storageService.getLocalStorage( LOCAL_STORAGE.LANGUAGE );
		if(!browserLanguage) {
			browserLanguage = window.navigator["userLanguage"] || window.navigator.language;
		}
		return browserLanguage;
	}

	clear() {
		this.storageService.deleteLocalStorage('idToken');
		this.storageService.deleteLocalStorage('tender');
		this._userType = undefined;
		this._userId = undefined;
		this._currentOrg = undefined;
		this._permissions = undefined;
		this._refreshTokenExpiration = 0;
		this._accessTokenExpirationTime = 0;
	}
}
